import {
    addDays,
    addHours,
    addMilliseconds,
    addMinutes,
    addMonths,
    isAfter,
    isEqual,
} from "date-fns";

import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { PromiseLruCache } from "@formant/core/common/PromiseLruCache";
import { TooMuchDataError } from "@formant/core/common/TooMuchDataError";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { duration } from "@formant/core/model/duration";
import { IsoDate } from "@formant/core/model/IsoDate";

import { days, hours, minutes, months } from "../utils/time";

const recentDuration = 10 * duration.second;

/**
 * A cache where "live" results are more frequently updated than historical data.
 * This is accomplished by segmenting the results by minute/hour/day partitions where "recent" partitions have a lower cache TTL.
 */
export abstract class SegmentCache<
    Query extends { start?: IsoDate; end?: IsoDate },
    Result,
> {
    private recentSegmentCache = new PromiseLruCache<Query, Result[]>({
        name: "SegmentCache-recentSegmentCache",
        max: 10000,
        ttl: 500 * duration.millisecond,
        fastStringify: true,
    });
    private segmentCache = new PromiseLruCache<Query, Result[]>({
        name: "SegmentCache-segmentCache",
        max: 10000,
        ttl: 15 * duration.minute,
        fastStringify: true,
    });
    private tooMuchDataCache = new PromiseLruCache<Query, boolean>({
        name: "SegmentCache-tooMuchDataCache",
        max: 1000,
        ttl: 5 * duration.second,
        fastStringify: true,
    });

    constructor(protected segmentSize: "minute" | "hour" | "day" | "month") {}

    public async query(token: string, query: Query): Promise<Result[]> {
        if (!query.start || !query.end) {
            return this.fetch(token, query);
        }
        const segments = this.segments(
            parseIsoDate(query.start),
            parseIsoDate(query.end),
        );

        const segmentData = segments.map(_ => this.getSegment(_, query));

        const missing = this.mergeSegments(
            segments.filter((_, i) => segmentData[i] === undefined),
        );

        if (missing.length > 0) {
            await Promise.all(
                missing.map(({ start, end }) =>
                    this.doFetch(start, end, token, query),
                ),
            );
            // Some more segments may have expired so try again
            return await this.query(token, query);
        }

        return this.mergeResults(await Promise.all(segmentData.map(defined)));
    }

    public clear(): void {
        this.segmentCache.clear();
        this.recentSegmentCache.clear();
        this.tooMuchDataCache.clear();
    }

    protected abstract fetch(token: string, query: Query): Promise<Result[]>;

    protected abstract mergeResults(allResults: Result[][]): Result[];

    protected abstract segmentResult(
        start: Date,
        end: Date,
        results: Result[],
    ): Result[];

    private async doFetch(start: Date, end: Date, token: string, query: Query) {
        const intervalQuery: Query = {
            ...query,
            start: start.toISOString(),
            end: end.toISOString(),
        };

        if (await this.tooMuchDataCache.get(intervalQuery)) {
            throw new TooMuchDataError();
        }

        const fetched = this.fetch(token, intervalQuery);

        this.tooMuchDataCache.set(
            intervalQuery,
            fetched
                .then(_ => false)
                .catch(error => {
                    narrowError(error, TooMuchDataError);
                    return true;
                }),
        );

        const segments = this.segments(start, end);
        segments.forEach(segment =>
            this.putSegment(
                segment,
                query,
                fetched.then(result =>
                    this.segmentResult(
                        segment,
                        this.nextSegment(segment),
                        result,
                    ),
                ),
            ),
        );
    }

    private segments(start: Date, end: Date): Date[] {
        switch (this.segmentSize) {
            case "minute":
                return [...minutes(start, end)];
            case "hour":
                return [...hours(start, end)];
            case "day":
                return [...days(start, end)];
            case "month":
                return [...months(start, end)];
        }
    }

    private nextSegment(segment: Date): Date {
        switch (this.segmentSize) {
            case "minute":
                return addMinutes(segment, 1);
            case "hour":
                return addHours(segment, 1);
            case "day":
                return addDays(segment, 1);
            case "month":
                return addMonths(segment, 1);
        }
    }

    private getSegment(
        segment: Date,
        query: Query,
    ): Promise<Result[]> | undefined {
        const key = {
            ...query,
            start: segment.toISOString(),
            end: this.nextSegment(segment).toISOString(),
        };

        const recent = this.recentSegmentCache.get(key);
        if (recent) {
            return recent;
        }

        const result = this.segmentCache.get(key);
        // if our result was from a region that is updating (e.g. on demand
        // is updating the past) and we didnt find in recent, delete cache
        // so we start putting it in recent
        if (result && this.isSegmentUpdating(segment, query)) {
            this.segmentCache.delete(key);
            return undefined;
        }
        return result;
    }

    private putSegment(segment: Date, query: Query, data: Promise<Result[]>) {
        const key = {
            ...query,
            start: segment.toISOString(),
            end: this.nextSegment(segment).toISOString(),
        };

        // live data and segments that are updating should be put in recent segment caches
        if (
            this.isSegmentUpdating(segment, query) ||
            isAfter(
                addMilliseconds(this.nextSegment(segment), recentDuration),
                new Date(),
            )
        ) {
            this.recentSegmentCache.set(key, data);
        } else {
            this.segmentCache.set(key, data);
        }
    }

    protected isSegmentUpdating(_segment: Date, _query: Query): boolean {
        // by default our segment caches never check if they are updating
        return false;
    }

    private mergeSegments(segments: Date[]): { start: Date; end: Date }[] {
        return segments.reduce<{ start: Date; end: Date }[]>((acc, segment) => {
            const [last, ...rest] = acc;

            if (last && isEqual(last.end, segment)) {
                return [{ start: last.start, end: this.nextSegment(segment) }];
            }

            return [
                { start: segment, end: this.nextSegment(segment) },
                ...rest,
            ];
        }, []);
    }
}
